"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.LocaleEntry = exports.LocalizationContext = void 0;
const dayjs_1 = __importDefault(require("dayjs"));
const lodash_1 = require("lodash");
const i18next_1 = __importDefault(require("i18next"));
const IS_SSR = typeof window === 'undefined';
class LocalizationContext {
    get currentLocale() {
        return this.locales[this.i18next.language];
    }
    constructor(parsedDefinition, localizationDataProvider) {
        this.localesInitialized = {};
        this.defaultLocale = parsedDefinition.defaultLocale;
        this.locales = Object.fromEntries(Object.entries(parsedDefinition.locales).map(([localeCode, def]) => [localeCode, new LocaleEntry(def)]));
        this.localizationDataProvider = localizationDataProvider;
        this.i18next = i18next_1.default.createInstance();
    }
    setLocale(locale) {
        if (!(locale in this.locales))
            throw new Error(`Invalid locale code: ${locale}`);
        if (!this.i18next.isInitialized) {
            this.init(locale);
        }
        else {
            this.loadLocaleData(locale);
            if (locale !== this.i18next.language)
                this.i18next.changeLanguage(locale);
        }
    }
    init(locale) {
        const initialLocale = locale || this.defaultLocale;
        this.i18next.init({
            lng: initialLocale,
            defaultNS: 'default',
            supportedLngs: (0, lodash_1.keys)(this.locales),
            initImmediate: false,
            compatibilityJSON: 'v3',
            react: {
                useSuspense: false,
            },
            interpolation: {
                escapeValue: false,
                format: function (value, format) {
                    if (value instanceof Date)
                        return (0, dayjs_1.default)(value).format(format);
                    return value;
                },
            },
        });
        // In SSR, we need access to all locales, all the time; preload them now
        if (IS_SSR) {
            for (const l of Object.keys(this.locales))
                this.loadLocaleData(l);
        }
        else {
            this.loadLocaleData(initialLocale);
        }
        return this;
    }
    loadLocaleData(locale) {
        if (this.localesInitialized[locale] !== true) {
            const data = this.localizationDataProvider(locale);
            for (const namespace of Object.keys(data)) {
                this.i18next.addResourceBundle(locale, namespace, data[namespace], true, true);
            }
            this.locales[locale].init(this.i18next);
            this.localesInitialized[locale] = true;
        }
    }
    localizePath(path) {
        return Object.fromEntries(Object.values(this.locales).map((locale) => [
            locale.localeCode,
            locale.localizePath(path),
        ]));
    }
}
exports.LocalizationContext = LocalizationContext;
class LocaleEntry {
    get t() {
        return this._t;
    }
    constructor(definition) {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        this._t = () => { };
        this.localizePath = (path) => {
            try {
                const localized = this.localizePathSegment(path);
                // basePath doesn't end with a slash
                return (this.basePath +
                    ((localized === null || localized === void 0 ? void 0 : localized.charAt(0)) === '/' ? '' : '/') +
                    localized);
            }
            catch (err) {
                throw new Error(`Failed to localize: ${path} - ${err}`);
            }
        };
        this.localizePathSegment = (path) => {
            if (Array.isArray(path))
                return path
                    .map((segment) => this.localizePathSegment(segment))
                    .join('/');
            if (typeof path === 'function')
                return path(this.localeCode);
            if (typeof path === 'object') {
                return this.localizePathSegment(path[this.localeCode]);
            }
            if (typeof path === 'string') {
                if (path.indexOf('/') >= 0) {
                    return path
                        .split('/')
                        .map((segment) => this.localizePathSegment(segment))
                        .join('/');
                }
                else {
                    // try to find translation in the URL context, otherwise fallback on staticUrl context
                    return this._t(`url:${path}`, this._t(`staticUrl:${path}`, path));
                }
            }
            throw new Error(`Failed to localize: ${path}`);
        };
        this.localeCode = definition.localeCode;
        this.bcp47LocaleCode = definition.bcp47LocaleCode;
        this.langCode = definition.langCode;
        this.countryCode = definition.countryCode;
        this.basePath = definition.basePath;
    }
    init(i18next) {
        this._t = i18next.getFixedT(this.localeCode);
    }
}
exports.LocaleEntry = LocaleEntry;
//# sourceMappingURL=localization-context.js.map